import React, { useMemo, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import Notification from '@this/src/notification';
import { FEEDBACK_TYPES, type FeedbackType } from '../../types/messages';
import { updateAiMessageFeedback } from '../../api/messages';
import FeedbackCommentPopup from './FeedbackCommentPopup';

type Props = {
  messageId: string;
  tripId: string;
  currentFeedback: FeedbackType | null;
  feedbackCommentSubmitted: boolean;
  onFeedbackUpdate: (messageId: string, feedback: FeedbackType, commentSubmitted: boolean) => void;
};

const FEEDBACK_OPTIONS: Record<
  FeedbackType,
  {
    buttonLabel: string;
    selectedLabel: string;
    filledIcon: typeof ThumbUpIcon;
    outlinedIcon: typeof ThumbUpOutlinedIcon;
    promptText: string;
  }
> = {
  good: {
    buttonLabel: 'good',
    selectedLabel: 'good-selected',
    filledIcon: ThumbUpIcon,
    outlinedIcon: ThumbUpOutlinedIcon,
    promptText: 'よろしければ、サービス品質向上のために、よかった点をお聞かせください'
  },
  bad: {
    buttonLabel: 'bad',
    selectedLabel: 'bad-selected',
    filledIcon: ThumbDownIcon,
    outlinedIcon: ThumbDownOutlinedIcon,
    promptText: 'よろしければ、サービス品質向上のために、どのような返答が望ましかったかをお聞かせください'
  }
};

const AiMessageFeedback = ({
  messageId,
  tripId,
  currentFeedback,
  feedbackCommentSubmitted,
  onFeedbackUpdate
}: Props) => {
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [popupTarget, setPopupTarget] = useState<FeedbackType | null>(null);

  const handleRatingClick = async (feedbackType: FeedbackType) => {
    if (currentFeedback === null) {
      if (isSubmittingRating) return;
      setIsSubmittingRating(true);
      try {
        const result = await updateAiMessageFeedback(tripId, messageId, feedbackType);
        onFeedbackUpdate(messageId, feedbackType, result.feedback_comment_submitted);
        setPopupTarget(feedbackType);
      } catch {
        Notification.error('フィードバックの送信に失敗しました。時間をおいてもう一度お試しください。');
      } finally {
        setIsSubmittingRating(false);
      }
      return;
    }
    if (currentFeedback === feedbackType && !feedbackCommentSubmitted) {
      setPopupTarget(feedbackType);
    }
  };

  const handleSubmitComment = async (comment: string) => {
    if (popupTarget === null || isSubmittingComment) return;
    setIsSubmittingComment(true);
    try {
      const result = await updateAiMessageFeedback(tripId, messageId, popupTarget, comment);
      onFeedbackUpdate(messageId, popupTarget, result.feedback_comment_submitted);
      Notification.success('フィードバックありがとうございます');
      setPopupTarget(null);
    } catch {
      Notification.error('フィードバックの送信に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const styles = useMemo(() => getStyles(), []);

  return (
    <div style={styles.container}>
      {(Object.values(FEEDBACK_TYPES) as FeedbackType[]).map(feedbackType => {
        const { buttonLabel, selectedLabel, filledIcon, outlinedIcon } = FEEDBACK_OPTIONS[feedbackType];
        const isSelected = currentFeedback === feedbackType;

        if (isSelected) {
          const ariaLabel = selectedLabel;
          if (feedbackCommentSubmitted) {
            return (
              <div key={feedbackType} aria-label={ariaLabel} style={styles.selectedIcon}>
                {React.createElement(filledIcon, { style: styles.icon })}
              </div>
            );
          }
          return (
            <IconButton
              key={feedbackType}
              aria-label={ariaLabel}
              size="small"
              onClick={() => handleRatingClick(feedbackType)}
            >
              {React.createElement(filledIcon, { style: styles.icon })}
            </IconButton>
          );
        }

        if (currentFeedback !== null) return null;

        return (
          <IconButton
            key={feedbackType}
            aria-label={buttonLabel}
            size="small"
            disabled={isSubmittingRating}
            onClick={() => handleRatingClick(feedbackType)}
          >
            {React.createElement(outlinedIcon, { style: styles.icon })}
          </IconButton>
        );
      })}
      {popupTarget !== null && (
        <FeedbackCommentPopup
          promptText={FEEDBACK_OPTIONS[popupTarget].promptText}
          disabled={isSubmittingComment}
          onSubmit={handleSubmitComment}
          onClose={() => setPopupTarget(null)}
        />
      )}
    </div>
  );
};

const getStyles = () =>
  ({
    container: {
      position: 'relative',
      display: 'flex',
      gap: '4px',
      marginTop: '4px'
    },
    selectedIcon: {
      color: '#111111',
      display: 'flex',
      alignItems: 'center',
      padding: '4px'
    },
    icon: {
      fontSize: '16px'
    }
  } as const);

export default AiMessageFeedback;
